import React, { useEffect, useState } from 'react';
import { doctoralApi } from '@/features/workspace/api';
import { DoctoralDefenseData } from '../types';
import { DEFENSE_STEPS, OFFICIAL } from '../doctoralTemplate';
import { Card, Hero, SourceCard } from './dp120Parts';

interface DoctoralDefenseViewProps {
  data?: DoctoralDefenseData | null;
}

export const DoctoralDefenseView: React.FC<DoctoralDefenseViewProps> = ({ data }) => {
  const [active, setActive] = useState(0);
  const [plannedDate, setPlannedDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (typeof doctoralApi?.record !== 'function') return;
    doctoralApi
      .record()
      .then((rec) => {
        if (rec?.planned_defense_date) setPlannedDate(rec.planned_defense_date);
      })
      .catch(() => undefined);
  }, []);

  const saveDate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!plannedDate) return;
    setSaving(true);
    setNotice('');
    try {
      await doctoralApi.updatePlannedDefense({ planned_defense_date: plannedDate });
      setNotice('Планируемая дата защиты сохранена.');
    } catch {
      setNotice('Не удалось сохранить дату защиты.');
    } finally {
      setSaving(false);
    }
  };

  const stages =
    data?.stages?.length
      ? data.stages.map((stage) => ({
          title: stage.title,
          desc: stage.subtitle,
          docs: stage.items.map((item) => item.title),
        }))
      : DEFENSE_STEPS;
  const current = stages[active] || stages[0];

  return (
    <>
      <Hero
        kicker="FINAL ROUTE"
        title="Предзащита и защита"
        text="Отдельная последовательная дорожная карта от кафедральной предзащиты до оформления результата защиты."
        color="#9b7cff"
        actions={
          <a className="dp120-btn violet" href={OFFICIAL.defense} target="_blank" rel="noopener noreferrer">
            Диссертационный совет ENU ↗
          </a>
        }
      />
      {notice ? <div className="dp120-callout" style={{ marginTop: 10 }}><b>{notice}</b></div> : null}
      <div className="dp120-subtabs">
        {stages.map((stage, index) => (
          <button key={stage.title} type="button" className={index === active ? 'active' : ''} onClick={() => setActive(index)}>
            {index + 1}. {stage.title.replace(/^\d+\.\s*/, '')}
          </button>
        ))}
      </div>
      <div className="dp120-grid">
        <Card title={current.title} sub={current.desc} span="span12">
          <div className="dp120-checklist">
            {current.docs.map((doc, index) => (
              <div key={doc} className="dp120-check">
                <span className="num">{String(index + 1).padStart(2, '0')}</span>
                <span>
                  <strong>{doc}</strong>
                  <small>Файл и статус привязываются к этому этапу</small>
                </span>
                <span className="dp120-badge">По этапу</span>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Планируемая дата защиты" sub="Сохраняется в учебном деле." span="span6">
          <form className="dp120-form" onSubmit={saveDate}>
            <label className="dp120-field">
              <span>Дата</span>
              <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} />
            </label>
            <div className="dp120-course-actions">
              <button type="submit" className="dp120-mini gold" disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить дату'}
              </button>
            </div>
          </form>
        </Card>
        <Card title="Официальные требования" span="span6">
          <SourceCard
            title="Защита и нормативы"
            text="ENU публикует алгоритм защиты, правила присуждения степеней и регламент этической комиссии."
            links={[
              ['Диссертационный совет', OFFICIAL.defense],
              ['Нормативные документы', OFFICIAL.regulations],
              ['Правила №127', OFFICIAL.degree],
            ]}
          />
        </Card>
      </div>
    </>
  );
};
